// src/hooks/useMateriaFeedback.js
import { useCallback, useRef, useState } from 'react';

/**
 * Gestiona feedback no intrusivo: toast resumido, animación in-situ y (opcional) historial.
 * - Exponer notifyChange para registrar cambios de estado de materias.
 * - Mantiene un único toast que se actualiza y se autocierra.
 * - Marca materias como justUpdated por ~800ms para animación en el nodo.
 * - Guarda un historial acotado (10) de cambios recientes.
 */
export const useMateriaFeedback = () => {
  const [summary, setSummary] = useState({
    isVisible: false,
    totalChanges: 0,
    lastMateriaName: '',
    lastEstado: '',
  });
  const [recentlyChangedIds, setRecentlyChangedIds] = useState(() => new Set());
  const [history, setHistory] = useState([]);

  const hideTimerRef = useRef(null);

  /**
   * Registra un cambio de estado.
   * @param {{id:string,nombre:string}} materia
   * @param {string} nuevoEstado
   */
  const notifyChange = useCallback((materia, nuevoEstado) => {
    setSummary(prev => ({
      isVisible: true,
      totalChanges: (prev.totalChanges || 0) + 1,
      lastMateriaName: materia?.nombre || '',
      lastEstado: nuevoEstado || '',
    }));

    // Reiniciar autocierre (2.5s)
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setSummary(prev => ({ ...prev, isVisible: false }));
    }, 2500);

    // Marcar nodo como recién actualizado (para animación del borde / check)
    setRecentlyChangedIds(prev => {
      const next = new Set(prev);
      next.add(String(materia.id));
      return next;
    });
    // Limpiar marca tras 800ms
    setTimeout(() => {
      setRecentlyChangedIds(prev => {
        const next = new Set(prev);
        next.delete(String(materia.id));
        return next;
      });
    }, 800);

    // Historial (máx 10)
    setHistory(prev => {
      const entry = { id: materia.id, nombre: materia.nombre, estado: nuevoEstado, ts: Date.now() };
      const next = [entry, ...prev];
      return next.slice(0, 10);
    });
  }, []);

  const clearSummary = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setSummary(prev => ({ ...prev, isVisible: false }));
  }, []);

  return {
    // estado
    summary,
    recentlyChangedIds,
    history,

    // acciones
    notifyChange,
    clearSummary,
  };
};

export default useMateriaFeedback;


